import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const CustomerLockTrackerContext = createContext(null);

export function CustomerLockTrackerProvider({ children }) {
  const [sources, setSources] = useState({});

  const reportCustomer = useCallback((sourceId, customer) => {
    if (!sourceId) return;
    setSources((prev) => {
      const next = { ...prev };
      if (!customer?.profile_locked) {
        if (!(sourceId in next)) return prev;
        delete next[sourceId];
        return next;
      }
      const prevCustomer = prev[sourceId];
      if (
        prevCustomer?.id === customer.id &&
        prevCustomer?.profile_locked === customer.profile_locked &&
        prevCustomer?.lock_reason === customer.lock_reason &&
        prevCustomer?.locked_at === customer.locked_at
      ) {
        return prev;
      }
      next[sourceId] = customer;
      return next;
    });
  }, []);

  const clearSource = useCallback((sourceId) => {
    if (!sourceId) return;
    setSources((prev) => {
      if (!(sourceId in prev)) return prev;
      const next = { ...prev };
      delete next[sourceId];
      return next;
    });
  }, []);

  const lockedCustomers = useMemo(() => {
    const byId = {};
    Object.values(sources).forEach((customer) => {
      if (customer?.id) byId[customer.id] = customer;
    });
    return Object.values(byId);
  }, [sources]);

  const value = useMemo(
    () => ({ reportCustomer, clearSource, lockedCustomers }),
    [reportCustomer, clearSource, lockedCustomers]
  );

  return (
    <CustomerLockTrackerContext.Provider value={value}>
      {children}
    </CustomerLockTrackerContext.Provider>
  );
}

export function useCustomerLockTracker() {
  return useContext(CustomerLockTrackerContext);
}

export function useReportLockedCustomer(sourceId, customer) {
  const tracker = useCustomerLockTracker();

  useEffect(() => {
    if (!tracker || !sourceId) return undefined;
    tracker.reportCustomer(sourceId, customer);
    return () => tracker.clearSource(sourceId);
  }, [
    tracker,
    sourceId,
    customer?.id,
    customer?.profile_locked,
    customer?.lock_reason,
    customer?.locked_at,
  ]);
}

export function customerIsLocked(customer) {
  return Boolean(customer?.profile_locked);
}

export const CUSTOMER_LOCK_REASON_MAX = 500;

// Create: any locked selection blocks submit.
// Edit: only block when switching to a *different* locked customer.
export function customerLockBlocksSubmit(
  values,
  { originalCustomerId, name = "customer_id" } = {}
) {
  if (!values) return false;
  const locked = Boolean(
    values[`${name}_locked`] || values.customer?.profile_locked
  );
  if (!locked) return false;
  const selectedId = values[name] ?? values.customer?.id;
  if (
    originalCustomerId != null &&
    selectedId != null &&
    String(selectedId) === String(originalCustomerId)
  ) {
    return false;
  }
  return true;
}
